import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import AppButton from "../../common/AppButton";

export type StudentExitAction = "withdraw" | "graduate" | "transferOut";

export interface StudentExitDialogProps {
  open: boolean;
  action: StudentExitAction;
  studentName: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}

const StudentExitDialog: React.FC<StudentExitDialogProps> = ({
  open,
  action,
  studentName,
  loading = false,
  onClose,
  onConfirm
}) => {
  const { t } = useKivunovaTranslation();
  const [reason, setReason] = useState("");

  const copy = {
    withdraw: {
      title: t("withdraw-student-title", "Withdraw {{name}}", {
        name: studentName
      }),
      warning: t(
        "withdraw-student-warning",
        "The student will be excluded from future billing. Existing invoices remain payable."
      ),
      confirm: t("withdraw-student", "Withdraw")
    },
    graduate: {
      title: t("graduate-student-title", "Graduate {{name}}", {
        name: studentName
      }),
      warning: t(
        "graduate-student-warning",
        "Marks the student as graduated and excludes them from new billing."
      ),
      confirm: t("graduate-student", "Graduate")
    },
    transferOut: {
      title: t("transfer-student-title", "Transfer out {{name}}", {
        name: studentName
      }),
      warning: t(
        "transfer-student-warning",
        "Marks the student as transferred to another institution."
      ),
      confirm: t("transfer-student", "Transfer out")
    }
  }[action];

  const handleClose = () => {
    setReason("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {copy.warning}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={t("exit-reason", "Reason (optional)")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <AppButton variant="text" onClick={handleClose} disabled={loading}>
          {t("cancel", "Cancel")}
        </AppButton>
        <AppButton
          variant="danger"
          disabled={loading}
          onClick={async () => {
            await onConfirm(reason.trim());
            setReason("");
          }}
        >
          {copy.confirm}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default StudentExitDialog;
